"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RedemptionServiceStub = void 0;
/* eslint-disable @typescript-eslint/no-unused-vars */
const faker_1 = __importDefault(require("faker"));
const mystake_common_sdk_1 = require("@redbelly-tokeniser/mystake-common-sdk");
const ok = (payload) => ({ status: true, message: "ok", payload });
/** Cents per unit; the fixtures below are 2,500,000c over 2500 units. */
const STUB_REDEMPTION_PRICE_CENTS = 1000;
const resolveUnits = (basis, requestedValue) => basis === "Units" ? requestedValue : Math.floor(requestedValue / STUB_REDEMPTION_PRICE_CENTS);
const stubSettings = () => ({
    allowRedemptions: true,
    dealingFrequency: mystake_common_sdk_1.DealingFrequency.MONTHLY,
    cutoff: { weekday: 2, time: "14:00", timezone: "Australia/Sydney" },
    minimumRedemptionUnits: 100,
    minimumRemainingHoldingUnits: 50,
    roundingMethod: mystake_common_sdk_1.RoundingMethod.ROUND_DOWN,
    payoutMethod: mystake_common_sdk_1.RedemptionPayoutMethod.ABA,
});
// Completeness is derived, not asserted, so a stub-driven UI can reach the settings-incomplete state.
const settingsResponse = (settings) => ({
    settings,
    isComplete: (0, mystake_common_sdk_1.isRedemptionSettingsComplete)(settings),
    missingFields: (0, mystake_common_sdk_1.getMissingRedemptionSettingsFields)(settings),
    // The stub holds no requests, so nothing is locked; a stub-driven UI edits freely.
    lockedFields: [],
});
const stubRedemption = (companyId, overrides = {}) => ({
    _id: `redemptions-${faker_1.default.random.uuid()}`,
    companyId,
    shareholderId: `shareholders-${faker_1.default.random.uuid()}`,
    entityType: "individual",
    unitClassId: `shareclasses-${faker_1.default.random.uuid()}`,
    unitsHeldAtRequest: 10000,
    basis: "Amount",
    requestedValue: 2500000,
    unitsRequested: 2500,
    receivedAt: new Date("2026-08-01"),
    status: "requestReceived",
    // Mirrors the odm schema default, so a stub row and a real response agree.
    outOfBand: false,
    events: [],
    createdAt: new Date("2026-08-01"),
    updatedAt: new Date("2026-08-01"),
    ...overrides,
});
const emptyTotals = () => ({
    all: { count: 1, totalUnits: 2500, totalValueCents: 2500000 },
    requested: { count: 1, totalUnits: 2500, totalValueCents: 2500000 },
    pendingApproval: { count: 0, totalUnits: 0, totalValueCents: 0 },
    awaitingPayment: { count: 0, totalUnits: 0, totalValueCents: 0 },
    paidOob: { count: 0, totalUnits: 0, totalValueCents: 0 },
    cancelled: { count: 0, totalUnits: 0, totalValueCents: 0 },
});
const stubAbaFile = () => ({
    fileName: "redemptions.aba",
    content: Buffer.from("stub-aba-file").toString("base64"),
    remitterName: faker_1.default.company.companyName(),
    apcaId: "123456",
    creditRecordCount: 1,
    fileTotalCents: 2500000,
});
class RedemptionServiceStub {
    async getRedemptionSettings(_companyId) {
        return ok(settingsResponse(stubSettings()));
    }
    async updateRedemptionSettings(_companyId, settings) {
        return ok(settingsResponse({ ...stubSettings(), ...settings }));
    }
    async createRedemption(companyId, shareholderId, body) {
        return ok(stubRedemption(companyId, {
            shareholderId,
            unitClassId: body.unitClassId,
            basis: body.basis,
            requestedValue: body.requestedValue,
            unitsRequested: resolveUnits(body.basis, body.requestedValue),
        }));
    }
    async getRedemptions(companyId, query) {
        const totals = emptyTotals();
        const row = {
            ...stubRedemption(companyId, query?.shareholderId ? { shareholderId: query.shareholderId } : {}),
            investorName: faker_1.default.name.findName(),
            unitClassName: "Class A",
        };
        const filter = query?.filter ?? "all";
        return ok({
            items: totals[filter].count > 0 ? [row] : [],
            totals,
            window: {
                currentDealingDayKey: "2026-08-31",
                windowStartDate: new Date("2026-08-01"),
                cutoffDate: new Date("2026-08-25"),
                dealingDate: new Date("2026-08-31"),
                nextDealingDayKey: "2026-09-30",
                nextDealingDate: new Date("2026-09-30"),
                isClosed: false,
            },
        });
    }
    async getShareholderRedemptions(companyId, shareholderId) {
        // One row per stage, so a stub-backed tracker shows every step rather than one row repeated.
        return ok(["requestReceived", "approvedInPrinciple", "approved", "completed"].map((status, index) => ({
            ...stubRedemption(companyId, { _id: `redemptions-${index + 1}`, shareholderId, status }),
            investorName: faker_1.default.name.findName(),
            unitClassName: "Class A",
        })));
    }
    async getRedemption(companyId, redemptionId) {
        return ok({
            ...stubRedemption(companyId, { _id: redemptionId }),
            investorName: faker_1.default.name.findName(),
            shareClassCode: "ORD",
        });
    }
    async recordRedemptionVaultTransaction(_companyId, _shareholderId, _redemptionId, _txHash) {
        // The stub has no vault, so it has no transaction to record.
        return { status: false, message: "The stub records no vault transactions.", payload: null };
    }
    async cancelRedemption(companyId, shareholderId, redemptionId) {
        const cancelled = stubRedemption(companyId, { _id: redemptionId, shareholderId, status: "cancelled" });
        // Stage timestamps only, as the real response carries — the fund's own notes stay out of it.
        return ok({ ...cancelled, events: cancelled.events.map(({ at, key }) => ({ at, key })) });
    }
    async acceptRedemption(companyId, redemptionId, body) {
        // The real API derives the dealing day, so the stub derives one too rather than leaving it unset.
        return ok(stubRedemption(companyId, {
            _id: redemptionId,
            status: "approvedInPrinciple",
            anticipatedRedemptionDate: (0, mystake_common_sdk_1.nextDealingDate)(stubSettings(), new Date()) ?? undefined,
        }));
    }
    async rejectRedemption(companyId, redemptionId, body) {
        return ok(stubRedemption(companyId, { _id: redemptionId, status: "rejected", outcomeNote: body.reason }));
    }
    async decideOutOfBand(companyId, redemptionId, body) {
        if (body.decision === "decline") {
            return ok(stubRedemption(companyId, { _id: redemptionId, status: "rejected", outcomeNote: body.reason }));
        }
        return ok(stubRedemption(companyId, {
            _id: redemptionId,
            status: "approved",
            outOfBand: true,
            outcomeNote: body.reason,
            paymentDate: body.paymentDate,
        }));
    }
    async approveWindow(_companyId, windowKey, _body) {
        return ok({ windowKey, approvedCount: 1, totalNetPayableCents: 2500000, abaFileRef: "aba-stub" });
    }
    async completeWindow(_companyId, windowKey) {
        return ok({ windowKey, completedCount: 1, totalNetPayableCents: 2500000 });
    }
    async confirmPaymentMade(companyId, redemptionId) {
        return ok(stubRedemption(companyId, { _id: redemptionId, status: "completed" }));
    }
    async getWindowAbaFile(_companyId, _windowKey) {
        return ok(stubAbaFile());
    }
    getWindowManualPayments(_companyId, _windowKey) {
        return Promise.resolve(ok({ rows: [], totalCents: 0 }));
    }
    async getRedemptionAbaFile(_companyId, _redemptionId) {
        return ok(stubAbaFile());
    }
    async getRedemptionNotice(_companyId, _shareholderId, _redemptionId, _timestamp) {
        return ok(Buffer.from("%PDF-1.4 stub").toString("base64"));
    }
    async getRedemptionTransactions(companyId) {
        const redemption = stubRedemption(companyId, { status: "approved", netPayableCents: 2500000 });
        return ok({
            rows: [
                {
                    redemptionId: redemption._id,
                    shareholderId: redemption.shareholderId,
                    investorName: faker_1.default.name.findName(),
                    unitClassId: redemption.unitClassId,
                    units: redemption.unitsRequested,
                    netPayableCents: 2500000,
                    status: "approved",
                },
            ],
            totalNetPayableCents: 2500000,
        });
    }
}
exports.RedemptionServiceStub = RedemptionServiceStub;
//# sourceMappingURL=RedemptionServiceStub.js.map